import { Injectable } from '@angular/core';
import { AuctionItem, HorseStatus, DisplayPrice } from '../../buyer-modules/buyer-auctions/_interfaces/auction.interface';

/**
 * Centralized service for horse status display logic
 *
 * This service acts as a zero-logic wrapper around backend data.
 * All business logic is handled by the Laravel API - this service
 * simply extracts and formats the data for display.
 *
 * **IMPORTANT**: Do NOT add business logic here. The backend API
 * (HorseStatusPriceService in Laravel) is the single source of truth.
 */
@Injectable({
  providedIn: 'root'
})
export class HorseStatusDisplayService {

  // ==================== STATUS DISPLAY ====================

  /**
   * Get translation key for status label
   * Returns null if no status (active auction)
   */
  getStatusLabel(item: Partial<AuctionItem>): string | null {
    const normalizedStatus = this.getNormalizedStatus(item);
    if (!normalizedStatus) {
      // Backward compatibility: treat withdrawn_at as withdrawn status
      return item.withdrawn_at != null ? 'status.withdrawn' : null;
    }

    // Convert status to translation key format
    // Example: "not sold" -> "status.not_sold"
    return `status.${normalizedStatus}`;
  }

  /**
   * Get CSS class for status badge styling
   */
  getStatusClass(item: Partial<AuctionItem>): string {
    const classMap: Record<string, string> = {
      'ps': 'status-ps',
      'winner': 'status-winner',
      'sold': 'status-sold',
      'rna': 'status-rna',
      'not_sold': 'status-not-sold',
      'withdrawn': 'status-withdrawn',
    };
    const normalizedStatus = this.getNormalizedStatus(item);
    if (!normalizedStatus && item.withdrawn_at != null) {
      return classMap['withdrawn'];
    }

    return normalizedStatus ? classMap[normalizedStatus] || '' : '';
  }

  /**
   * Check if status badge should be displayed
   * Returns false for active auctions (null status)
   */
  shouldShowStatus(item: Partial<AuctionItem>): boolean {
    return !!this.getNormalizedStatus(item) || item.withdrawn_at != null;
  }

  /**
   * Check if this is a PS (Post Sale / Offline) status
   */
  isPS(item: Partial<AuctionItem>): boolean {
    return this.getNormalizedStatus(item) === HorseStatus.PS;
  }

  /**
   * Check if this is RNA status
   */
  isRNA(item: Partial<AuctionItem>): boolean {
    return this.getNormalizedStatus(item) === HorseStatus.RNA;
  }

  /**
   * Check if this is Sold status
   */
  isSold(item: Partial<AuctionItem>): boolean {
    return this.getNormalizedStatus(item) === HorseStatus.Sold;
  }

  /**
   * Check if this is Winner status
   */
  isWinner(item: Partial<AuctionItem>): boolean {
    return this.getNormalizedStatus(item) === HorseStatus.Winner;
  }

  /**
   * Check if this is Not Sold status
   */
  isNotSold(item: Partial<AuctionItem>): boolean {
    return this.getNormalizedStatus(item) === HorseStatus.NotSold;
  }

  /**
   * Check if this is Withdrawn status
   */
  isWithdrawn(item: Partial<AuctionItem>): boolean {
    return this.getNormalizedStatus(item) === HorseStatus.Withdrawn || item.withdrawn_at != null;
  }

  /**
   * Check if this is any admin "No Status" override variant.
   * All variants render as ended with NO status badge.
   * Price handling differs:
   *  - no_status / no_status_dash_price -> price shown as "----"
   *  - no_status_hide_price             -> price row hidden entirely
   */
  isNoStatus(item: Partial<AuctionItem>): boolean {
    const s = this.getNormalizedStatus(item);
    return s === HorseStatus.NoStatus
      || s === HorseStatus.NoStatusHidePrice
      || s === HorseStatus.NoStatusDashPrice;
  }

  /**
   * Admin "No Status" override that HIDES the price row entirely (no "----").
   */
  isNoStatusHidePrice(item: Partial<AuctionItem>): boolean {
    return this.getNormalizedStatus(item) === HorseStatus.NoStatusHidePrice;
  }

  /**
   * Admin "No Status (sale)" override: no badge, but shows "Latest Bid" + "Highest Bidder"
   * (legacy pre-badge display). NOT part of isNoStatus() because the price row is real, not "----".
   */
  isNoStatusSale(item: Partial<AuctionItem>): boolean {
    return this.getNormalizedStatus(item) === HorseStatus.NoStatusSale;
  }

  // ==================== PRICE DISPLAY ====================

  /**
   * Get display price object from backend
   * Returns null if no price should be displayed
   */
  getDisplayPrice(item: Partial<AuctionItem>): DisplayPrice | null {
    return item.display_price || null;
  }

  /**
   * Get price label translation key
   * Returns null if no price to display
   */
  getPriceLabel(item: Partial<AuctionItem>): string | null {
    if (!item.display_price?.label) return null;

    // Convert label to translation key
    // Example: "selling_price" -> "price_label.selling_price"
    return `price_label.${item.display_price.label}`;
  }

  /**
   * Get formatted price string (with currency)
   * Returns null if no price to display
   */
  getFormattedPrice(item: Partial<AuctionItem>): string | null {
    const price = item.display_price;
    if (!price) return null;

    // Use locale-specific number formatting
    const formattedAmount = price.amount.toLocaleString();
    return `${formattedAmount} ${price.currency}`;
  }

  /**
   * Get price amount only (without currency)
   */
  getPriceAmount(item: Partial<AuctionItem>): number | null {
    return item.display_price?.amount ?? null;
  }

  /**
   * Get currency code
   */
  getCurrency(item: Partial<AuctionItem>): string | null {
    return item.display_price?.currency ?? null;
  }

  // ==================== BUYER NAME DISPLAY ====================

  /**
   * Get buyer name (already masked from backend if needed)
   * Returns null if no buyer name to display
   */
  getBuyerName(item: Partial<AuctionItem>): string | null {
    return item.buyer_name || null;
  }

  /**
   * Check if buyer name should be displayed
   * Don't show for withdrawn items or when no name exists
   */
  shouldShowBuyerName(item: Partial<AuctionItem>): boolean {
    return !!(item.buyer_name && !this.isWithdrawn(item));
  }

  // ==================== LEGACY COMPATIBILITY ====================

  /**
   * Get RNA status (legacy field)
   * Use this for backwards compatibility only
   * @deprecated Use shouldShowStatus() and isRNA() instead
   */
  getRNAStatus(item: Partial<AuctionItem>): boolean {
    return item.rna_status ?? false;
  }

  /**
   * Get highest bid amount (legacy field)
   * @deprecated Use getDisplayPrice() instead
   */
  getHighestBidAmount(item: Partial<AuctionItem>): number | null {
    return item.highest_bid?.amount ?? null;
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Check if auction item is in an ended state
   */
  hasEnded(item: Partial<AuctionItem>): boolean {
    return this.shouldShowStatus(item);
  }

  /**
   * Check if price should be hidden
   * True for Not Sold and Withdrawn statuses
   */
  shouldHidePrice(item: Partial<AuctionItem>): boolean {
    return !item.display_price;
  }

  /**
   * Normalize status values coming from backend.
   * Supports legacy values with spaces/casing differences.
   */
  private getNormalizedStatus(item: Partial<AuctionItem>): string | null {
    if (!item.status) return null;
    return String(item.status).toLowerCase().replace(/ /g, '_');
  }
}
